import { useLiveQuery } from '@electric-sql/pglite-react';
import {
  ageProblem,
  amountProblem,
  birthDateProblem,
  defaultDueDate,
  defaultSamples,
  describeDue,
  emailProblem,
  formatRupees,
  localDay,
  nameProblem,
  newId,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHODS,
  type PaymentMethod,
  phoneDigits,
  phoneProblem,
  SALUTATIONS,
  type Sample,
  type Sex,
  toPaise,
} from '@quickreport/domain';
import { Badge, Button, Field, Input, Select } from '@quickreport/ui';
import {
  addVisitPayment,
  ageFor,
  createReports,
  findPatients,
  getPatient,
  type PatientInput,
  type PatientRow,
  type TemplateRef,
} from '@quickreport/workspace';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { ArrowLeft, Check, Lock, Search, UserPlus } from 'lucide-react';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useNotifications } from '../../lib/notifications';
import { useWorkspace } from '../../lib/workspace';
import { loadTemplate, type TemplateListItem, templateListQuery } from '../../local-db/templates';
import { useCtx } from './context';
import { SamplesEditor } from './SamplesEditor';

interface Form {
  salutation: string;
  name: string;
  sex: Sex | '';
  ageAmount: string;
  ageUnit: 'Y' | 'M' | 'D';
  dob: string;
  useDob: boolean;
  phone: string;
  email: string;
  address: string;
  externalId: string;
}
const EMPTY: Form = {
  salutation: '',
  name: '',
  sex: '',
  ageAmount: '',
  ageUnit: 'Y',
  dob: '',
  useDob: false,
  phone: '',
  email: '',
  address: '',
  externalId: '',
};

function fromRow(p: PatientRow): Form {
  const a = ageFor(p);
  const m = /^(\d+)\s*([YMD])$/.exec(a.ageText);
  return {
    salutation: p.salutation ?? '',
    name: p.name,
    sex: (p.sex ?? '') as Sex | '',
    ageAmount: p.dob ? '' : (m?.[1] ?? ''),
    ageUnit: (m?.[2] as 'Y' | 'M' | 'D' | undefined) ?? 'Y',
    dob: p.dob ?? '',
    useDob: Boolean(p.dob),
    phone: p.phone ?? '',
    email: p.email ?? '',
    address: p.address ?? '',
    externalId: p.external_id ?? '',
  };
}

export function NewReportPage() {
  const ws = useWorkspace();
  const { planStatus } = useNotifications();
  // Only a spent-out wallet pauses new reports; an ended time plan is just a reminder.
  const planStopped = !planStatus.canCreateReports;
  const ctx = useCtx();
  const navigate = useNavigate();
  const showMoney = ws.can('view_billing');
  const takePayment = ws.can('manage_billing');

  // ---- patient
  const [q, setQ] = useState('');
  const dq = useDeferredValue(q);
  const [matches, setMatches] = useState<PatientRow[]>([]);
  const [patientId, setPatientId] = useState<string | undefined>();
  const [form, setForm] = useState<Form>(EMPTY);
  const [referredBy, setReferredBy] = useState('');
  const [now] = useState(() => new Date());
  // null = follow the chosen tests; once the person edits, the list is theirs.
  const [samples, setSamples] = useState<Sample[] | null>(null);
  const [received, setReceived] = useState('');
  // What the lab charges for the whole set. Empty means: use the prices on the templates.
  const [charge, setCharge] = useState('');
  const [changeCharge, setChangeCharge] = useState(false);
  // Problems are said beside the field once it has been left, or after pressing Create.
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [tried, setTried] = useState(false);
  const touch = (k: string) => setTouched((t) => (t[k] ? t : { ...t, [k]: true }));
  const [method, setMethod] = useState<PaymentMethod>('cash');
  useEffect(() => {
    let alive = true;
    findPatients(ws.db, ws.orgId, dq).then((r) => alive && setMatches(r));
    return () => {
      alive = false;
    };
  }, [ws.db, ws.orgId, dq]);

  // ---- tests
  const [tq, setTq] = useState('');
  const dtq = useDeferredValue(tq);
  const { sql, params } = templateListQuery(ws.orgId, dtq, { pageSize: 100 });
  const templates = useLiveQuery<TemplateListItem>(sql, params);
  const [picked, setPicked] = useState<string[]>([]);
  // What each chosen test asks for and costs, kept even when a new search hides its row.
  const [meta, setMeta] = useState<
    Record<string, { sample: string | null; price: number; turnaround: number | null }>
  >({});
  // null = follow the chosen tests (the slowest one sets the day).
  const [due, setDue] = useState<string | null>(null);
  const autoDue = useMemo(
    () =>
      defaultDueDate(
        picked.map((id) => ({ turnaroundDays: meta[id]?.turnaround ?? undefined })),
        localDay(now),
      ),
    [picked, meta, now],
  );
  const autoSamples = useMemo(() => {
    let n = 0;
    return defaultSamples(
      picked.map((id) => ({ sampleType: meta[id]?.sample ?? undefined })),
      now,
      () => `auto-${++n}`,
    );
  }, [picked, meta, now]);
  const shownSamples = samples ?? autoSamples;
  const totalPaise = picked.reduce((n, id) => n + toPaise(meta[id]?.price ?? 0), 0);
  const [generic, setGeneric] = useState(false);
  const [genericTitle, setGenericTitle] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }));

  // Arriving from the Patients tab: start with that patient filled in.
  const wanted = useSearch({ from: '/reports/new' }).patient;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `choose` only sets form state
  useEffect(() => {
    if (!wanted) return;
    void getPatient(ws.db, wanted).then((p) => p && choose(p));
  }, [wanted, ws.db]);

  function choose(p: PatientRow) {
    setPatientId(p.id);
    setForm(fromRow(p));
    setQ('');
  }

  async function submit() {
    setError(null);
    if (firstProblem) {
      setTried(true);
      // Let the errors render, then put the cursor in the first wrong field.
      window.setTimeout(() => {
        const el = document.querySelector<HTMLElement>(
          'main label:has([role="alert"]) :is(input,select), main [aria-invalid="true"], [data-tests-problem]',
        );
        el?.focus();
        el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 60);
      return;
    }
    if (!ctx.siteId)
      return setError(
        'This lab is still being set up on this computer. Wait a moment and try again.',
      );
    setBusy(true);
    try {
      const refs: TemplateRef[] = [];
      for (const id of picked) {
        const t = await loadTemplate(ws.db, id);
        if (t) refs.push({ templateId: id, version: t.version, doc: t.doc });
      }
      const amount = Number(form.ageAmount);
      const patient: PatientInput = {
        ...(patientId ? { id: patientId } : {}),
        ...(form.salutation ? { salutation: form.salutation } : {}),
        name: form.name,
        ...(form.sex ? { sex: form.sex } : {}),
        ...(form.useDob && form.dob ? { dob: form.dob } : {}),
        ...(!form.useDob && form.ageAmount && amount >= 0
          ? { age: { amount, unit: form.ageUnit } }
          : {}),
        phone: form.phone,
        email: form.email,
        address: form.address,
        externalId: form.externalId,
      };
      const ids = await createReports(ws.db, ctx, {
        siteId: ctx.siteId,
        patient,
        visit: { referredBy },
        samples: shownSamples.map((x) => ({ ...x, id: newId() })),
        ...(due ? { dueDate: due } : {}),
        templates: refs,
        ...(generic ? { generic: { title: genericTitle.trim() || 'Report' } } : {}),
        ...(takePayment && showMoney && (manualCharge || !allPriced) ? { chargePaise } : {}),
      });
      const paid = toPaise(Number(received) || 0);
      if (takePayment && paid > 0)
        await addVisitPayment(ws.db, ctx, ids, { amountPaise: paid, method });
      void ws.engine.syncNow();
      await navigate({ to: '/reports/$reportId', params: { reportId: ids[0] as string } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the report');
      setBusy(false);
    }
  }

  // Every chosen test has a price, so the total can be worked out for the person.
  const allPriced = picked.length > 0 && picked.every((id) => (meta[id]?.price ?? 0) > 0);
  const manualCharge = charge.trim() !== '' && amountProblem(charge) === null;
  const chargePaise = manualCharge ? toPaise(Number(charge)) : totalPaise;
  const problems: Record<string, string | null> = {
    name: nameProblem(form.name, 'the patient’s name'),
    age: form.useDob ? birthDateProblem(form.dob) : ageProblem(form.ageAmount, form.ageUnit),
    phone: phoneProblem(form.phone),
    email: emailProblem(form.email),
    tests: picked.length === 0 && !generic ? 'Choose at least one test' : null,
    charge: amountProblem(charge, 'amount to charge'),
    received: amountProblem(received, 'amount received'),
  };
  const shown = (k: string): string | null => {
    // Too many digits is wrong at once; there is no point waiting for the person to leave.
    if (k === 'phone' && phoneDigits(form.phone).length > 10) return problems.phone ?? null;
    return tried || touched[k] ? (problems[k] ?? null) : null;
  };
  const firstProblem = Object.values(problems).some(Boolean);
  const sensitivePicked = (templates?.rows ?? []).some((t) => picked.includes(t.id) && t.sensitive);

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-8">
      <div className="flex items-center gap-2">
        <Link
          to="/reports"
          className="rounded p-1.5 text-slate hover:bg-canvas"
          aria-label="Back to reports"
        >
          <ArrowLeft size={18} />
        </Link>
        <h1 className="text-xl font-semibold">New report</h1>
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-[3fr_2fr]">
        <section className="grid content-start gap-4" aria-label="Patient">
          <h2 className="text-base font-semibold">1 · Patient</h2>
          <div className="relative">
            <Search size={16} className="pointer-events-none absolute top-2 left-2.5 text-slate" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Find by mobile number or name"
              aria-label="Find a patient"
              className="h-9 pl-8"
            />
            {q.trim().length >= 2 ? (
              <ul className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-line bg-paper shadow-lg">
                {matches.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => choose(p)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-canvas"
                    >
                      <span>
                        <span className="font-medium">
                          {p.salutation ? `${p.salutation}. ` : ''}
                          {p.name}
                        </span>
                        <span className="block text-[12px] text-slate">
                          {[
                            ageFor(p).ageText,
                            p.sex === 'M' ? 'Male' : p.sex === 'F' ? 'Female' : '',
                            p.phone,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                      <Badge tone="brand">Use</Badge>
                    </button>
                  </li>
                ))}
                <li className="border-t border-line">
                  <button
                    type="button"
                    onClick={() => {
                      setPatientId(undefined);
                      setForm({ ...EMPTY, ...(/\d{4,}/.test(q) ? { phone: q } : { name: q }) });
                      setQ('');
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-brand hover:bg-canvas"
                  >
                    <UserPlus size={15} /> New patient “{q}”
                  </button>
                </li>
              </ul>
            ) : null}
          </div>
          {patientId ? (
            <p className="flex items-center gap-2 rounded-md bg-brand-soft px-3 py-1.5 text-[13px] text-brand">
              <Check size={14} /> Returning patient. Their details are filled in; change them if
              needed.
              <button
                type="button"
                className="ml-auto underline"
                onClick={() => {
                  setPatientId(undefined);
                  setForm(EMPTY);
                }}
              >
                Clear
              </button>
            </p>
          ) : null}

          <div className="grid grid-cols-[6rem_1fr] gap-3">
            <Field label="Title">
              <Select value={form.salutation} onChange={(e) => set({ salutation: e.target.value })}>
                <option value="" />
                {SALUTATIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Name" error={shown('name')}>
              <Input
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
                onBlur={() => touch('name')}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 items-start gap-3">
            <Field label="Sex">
              <Select value={form.sex} onChange={(e) => set({ sex: e.target.value as Sex | '' })}>
                <option value="" />
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="O">Other</option>
              </Select>
            </Field>
            <div className="grid min-w-0 gap-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[12px] font-medium text-slate">
                  {form.useDob ? 'Date of birth' : 'Age'}
                </span>
                <button
                  type="button"
                  className="text-[12px] text-brand hover:underline"
                  onClick={() => set({ useDob: !form.useDob })}
                >
                  {form.useDob ? 'Enter age instead' : 'Use date of birth'}
                </button>
              </div>
              {form.useDob ? (
                <Input
                  type="date"
                  value={form.dob}
                  onChange={(e) => set({ dob: e.target.value })}
                  onBlur={() => touch('age')}
                  aria-invalid={shown('age') ? true : undefined}
                  className={shown('age') ? 'border-flag' : ''}
                />
              ) : (
                <div className="flex gap-2">
                  <Input
                    inputMode="numeric"
                    value={form.ageAmount}
                    onChange={(e) => set({ ageAmount: e.target.value.replace(/\D/g, '') })}
                    onBlur={() => touch('age')}
                    aria-invalid={shown('age') ? true : undefined}
                    className={clsx('w-20', shown('age') && 'border-flag')}
                    aria-label="Age"
                  />
                  <Select
                    value={form.ageUnit}
                    onChange={(e) => set({ ageUnit: e.target.value as 'Y' | 'M' | 'D' })}
                    className="w-24"
                    aria-label="Age unit"
                  >
                    <option value="Y">years</option>
                    <option value="M">months</option>
                    <option value="D">days</option>
                  </Select>
                </div>
              )}
              {shown('age') ? (
                <span role="alert" className="text-[12px] font-medium text-flag">
                  {shown('age')}
                </span>
              ) : null}
            </div>
          </div>
          <div className="grid grid-cols-2 items-start gap-3">
            <Field
              label="Mobile"
              hint="10 digits. Reports are sent to this number."
              error={shown('phone')}
            >
              <Input
                value={form.phone}
                onChange={(e) => set({ phone: e.target.value })}
                onBlur={() => touch('phone')}
                inputMode="tel"
                placeholder="98765 43210"
              />
            </Field>
            <Field label="Email" error={shown('email')}>
              <Input
                value={form.email}
                onChange={(e) => set({ email: e.target.value })}
                onBlur={() => touch('email')}
                inputMode="email"
              />
            </Field>
          </div>
          <Field label="Address">
            <Input value={form.address} onChange={(e) => set({ address: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 items-start gap-3">
            <Field label="Referred by">
              <Input
                value={referredBy}
                onChange={(e) => setReferredBy(e.target.value)}
                placeholder="Dr. XYZ or Self"
              />
            </Field>
            <Field label="Patient ID" hint="Your own number, optional">
              <Input
                value={form.externalId}
                onChange={(e) => set({ externalId: e.target.value })}
              />
            </Field>
          </div>
          <SamplesEditor
            samples={shownSamples}
            // Ids stay as they are while typing so a field keeps its focus; real ids are made on create.
            onChange={setSamples}
            hint={samples ? undefined : 'Added from the tests; change them if needed'}
          />
        </section>

        <section className="grid content-start gap-4" aria-label="Tests">
          <h2 className="text-base font-semibold">2 · Tests</h2>
          {shown('tests') ? (
            <p
              role="alert"
              tabIndex={-1}
              data-tests-problem
              className="text-[13px] font-medium text-flag"
            >
              {shown('tests')}
            </p>
          ) : null}
          <div className="relative">
            <Search size={16} className="pointer-events-none absolute top-2 left-2.5 text-slate" />
            <Input
              value={tq}
              onChange={(e) => setTq(e.target.value)}
              placeholder="Search tests, e.g. hemogram, lipid, thyroid"
              aria-label="Search tests"
              className="h-9 pl-8"
            />
          </div>
          <ul className="max-h-[26rem] divide-y divide-line overflow-auto rounded-lg border border-line bg-paper">
            {(templates?.rows ?? []).map((t) => {
              const on = picked.includes(t.id);
              return (
                <li key={t.id}>
                  <label
                    className={clsx(
                      'flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-canvas',
                      on && 'bg-brand-soft/50',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => {
                        setPicked((p) => (on ? p.filter((x) => x !== t.id) : [...p, t.id]));
                        setMeta((m) => ({
                          ...m,
                          [t.id]: {
                            sample: t.sample_type,
                            price: t.price ?? 0,
                            turnaround: t.turnaround_days,
                          },
                        }));
                      }}
                      className="accent-brand"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="font-medium">{t.name}</span>
                      {t.sensitive ? (
                        <Lock
                          size={12}
                          className="ml-1.5 inline text-slate"
                          aria-label="Sensitive"
                        />
                      ) : null}
                      <span className="block text-[12px] text-slate">{t.department ?? ''}</span>
                    </span>
                    <span className="text-[12px] text-slate">
                      {showMoney && t.price != null ? `${formatRupees(toPaise(t.price))} · ` : ''}
                      {t.tests} tests
                    </span>
                  </label>
                </li>
              );
            })}
            <li>
              <label
                className={clsx(
                  'flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-canvas',
                  generic && 'bg-brand-soft/50',
                )}
              >
                <input
                  type="checkbox"
                  checked={generic}
                  onChange={() => setGeneric((g) => !g)}
                  className="accent-brand"
                />
                <span className="min-w-0 flex-1">
                  <span className="font-medium">Other (generic)</span>
                  <span className="block text-[12px] text-slate">
                    A blank report with your own title, details and table
                  </span>
                </span>
              </label>
            </li>
          </ul>
          {generic ? (
            <Field label="Report title">
              <Input
                value={genericTitle}
                onChange={(e) => setGenericTitle(e.target.value)}
                placeholder="e.g. Water analysis"
              />
            </Field>
          ) : null}
          {sensitivePicked ? (
            <p className="text-[13px] text-slate">
              Sensitive tests are never shown in a patient’s report list or in messages.
            </p>
          ) : null}
          <Field
            label="Report due"
            hint={`Promised for ${describeDue(due ?? autoDue, localDay(now))}. You get a reminder that day.`}
          >
            <Input
              type="date"
              value={due ?? autoDue}
              min={localDay(now)}
              onChange={(e) =>
                setDue(e.target.value && e.target.value !== autoDue ? e.target.value : null)
              }
              className="w-44"
            />
          </Field>
          {showMoney && takePayment ? (
            <section className="grid gap-2 rounded-lg border border-line p-3" aria-label="Payment">
              <div className="flex items-baseline justify-between">
                <h3 className="text-[13px] font-semibold">Payment</h3>
                <span className="text-[12px] text-slate">
                  Private to the lab. Never on the report.
                </span>
              </div>
              {allPriced && !changeCharge ? (
                <div className="flex items-baseline justify-between gap-3 rounded-md bg-canvas px-3 py-2">
                  <div>
                    <p className="text-[12px] text-slate">
                      Total for {picked.length} test{picked.length === 1 ? '' : 's'}
                    </p>
                    <p className="text-lg font-semibold tabular">{formatRupees(totalPaise)}</p>
                  </div>
                  <button
                    type="button"
                    className="text-[12px] text-brand hover:underline"
                    onClick={() => setChangeCharge(true)}
                  >
                    Charge a different amount
                  </button>
                </div>
              ) : (
                <Field
                  label="Amount to charge (₹)"
                  error={shown('charge')}
                  hint={
                    allPriced
                      ? `The tests add up to ${formatRupees(totalPaise)}. Leave empty to use that.`
                      : totalPaise > 0
                        ? `Some tests have no price. Priced ones add up to ${formatRupees(totalPaise)}. Enter what you charge for all of them.`
                        : 'What you charge for this set of tests. Optional.'
                  }
                >
                  <Input
                    inputMode="decimal"
                    value={charge}
                    onChange={(e) => setCharge(e.target.value.replace(/[^\d.]/g, ''))}
                    onBlur={() => touch('charge')}
                    placeholder={totalPaise > 0 ? String(totalPaise / 100) : 'For example 1200'}
                  />
                </Field>
              )}
              <div className="grid grid-cols-[1fr_9rem] gap-3">
                <Field
                  label="Amount received (₹)"
                  error={shown('received')}
                  hint={
                    chargePaise > 0
                      ? `Total ${formatRupees(chargePaise)}${
                          toPaise(Number(received) || 0) > 0
                            ? ` · balance ${formatRupees(Math.max(0, chargePaise - toPaise(Number(received) || 0)))}`
                            : ''
                        }`
                      : 'Optional. You can also add it later.'
                  }
                >
                  <Input
                    inputMode="decimal"
                    value={received}
                    onChange={(e) => setReceived(e.target.value.replace(/[^\d.]/g, ''))}
                    onBlur={() => touch('received')}
                  />
                </Field>
                <Field label="Paid by">
                  <Select
                    value={method}
                    onChange={(e) => setMethod(e.target.value as PaymentMethod)}
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {PAYMENT_METHOD_LABELS[m]}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            </section>
          ) : null}
          {error ? (
            <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-[13px] text-flag">
              {error}
            </p>
          ) : null}
          {planStopped ? (
            <p
              role="alert"
              className="rounded-md border border-flag/30 bg-red-50 px-3 py-2 text-[13px] text-flag"
            >
              Your wallet and credit are used up, so new reports are paused. Ask LabView to add
              credit. Reports you have already started can still be finished and signed.
            </p>
          ) : null}
          <div className="flex items-center gap-3">
            <Button tone="primary" disabled={busy || planStopped} onClick={() => void submit()}>
              {busy
                ? 'Creating…'
                : `Create ${picked.length + (generic ? 1 : 0) > 1 ? 'reports' : 'report'}`}
            </Button>
            <span className="text-[13px] text-slate">
              {picked.length + (generic ? 1 : 0)} test
              {picked.length + (generic ? 1 : 0) === 1 ? '' : 's'} chosen
            </span>
          </div>
        </section>
      </div>
    </div>
  );
}
